"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch } from "@/lib/api-client";
import type { AdminUser, Reservation } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users as UsersIcon,
  Building2,
  CalendarCheck,
  DollarSign,
  Pencil,
  Trash2,
  Check,
  X,
  Power,
  Shield,
} from "lucide-react";
import {
  formatDate,
  formatPrice,
  nightsBetween,
  roleBadgeClass,
  roleLabel,
  statusBadgeClass,
  statusLabel,
  nightLabel,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/lib/i18n/provider";
import { PermissionsMatrix } from "@/components/booking/permissions-matrix";
import { CurrencySetting } from "@/components/booking/settings-currency";

export function AdminDashboardView() {
  const user = useAppStore((s) => s.user);
  const openNewUser = useAppStore((s) => s.openNewUser);
  const openEditUser = useAppStore((s) => s.openEditUser);
  const openProduct = useAppStore((s) => s.openProduct);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState("overview");
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () =>
      apiGet<{
        users: number;
        products: number;
        reservations: {
          pending: number;
          approved: number;
          rejected: number;
          cancelled: number;
          total: number;
        };
        revenue: number;
        roleBreakdown: { role: string; _count: { _all: number } }[];
      }>("/api/admin/stats"),
    enabled: !!user && user.role === "ADMIN",
    refetchInterval: 15000,
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiGet<{ users: AdminUser[] }>("/api/admin/users"),
    enabled: !!user && user.role === "ADMIN",
  });

  const { data: resData } = useQuery({
    queryKey: ["reservations", "admin"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>("/api/reservations"),
    enabled: !!user && user.role === "ADMIN",
  });

  const users = usersData?.users ?? [];
  const reservations = resData?.reservations ?? [];
  const pendingRes = reservations.filter((r) => r.status === "PENDING");

  const decide = async (id: string, action: "approve" | "reject") => {
    try {
      await apiPatch(`/api/reservations/${id}`, { action });
      toast.success(action === "approve" ? t("toast.reservationApproved") : t("toast.reservationRejected"));
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    }
  };

  const toggleActive = async (u: AdminUser) => {
    try {
      await apiPatch(`/api/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? t("toast.userDisabled") : t("toast.userEnabled"));
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotUpdate"));
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await apiDelete(`/api/admin/users/${deleteUserId}`);
      toast.success(t("toast.userDeleted"));
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotDelete"));
    } finally {
      setDeleteUserId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Shield className="h-6 w-6 text-amber-600" /> {t("admin.title")}
          </h1>
          <p className="mt-1 text-muted-foreground">{t("admin.subtitle")}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("admin.tabOverview")}</TabsTrigger>
          <TabsTrigger value="users">
            {t("admin.tabUsers")} ({users.length})
          </TabsTrigger>
          <TabsTrigger value="reservations">
            {t("admin.tabReservations")} ({t("host.pendingCount", { count: pendingRes.length })})
          </TabsTrigger>
          <TabsTrigger value="permissions">{t("admin.tabPermissions")}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4">
          <Card className="mb-4">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <div className="text-sm font-medium">{t("settings.currencyTitle")}</div>
                <p className="text-xs text-muted-foreground">{t("settings.currencyHint")}</p>
              </div>
              <CurrencySetting />
            </CardContent>
          </Card>
          {statsLoading || !stats ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={UsersIcon}
                  label={t("admin.statUsers")}
                  value={stats.users}
                  color="text-sky-600 bg-sky-50"
                />
                <StatCard
                  icon={Building2}
                  label={t("admin.statProperties")}
                  value={stats.products}
                  color="text-emerald-600 bg-emerald-50"
                />
                <StatCard
                  icon={CalendarCheck}
                  label={t("admin.statReservations")}
                  value={stats.reservations.total}
                  color="text-violet-600 bg-violet-50"
                />
                <StatCard
                  icon={DollarSign}
                  label={t("admin.statRevenue")}
                  value={formatPrice(stats.revenue)}
                  color="text-amber-600 bg-amber-50"
                />
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t("admin.resByStatus")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <StatusTile label={statusLabel("PENDING", locale)} value={stats.reservations.pending} className={statusBadgeClass("PENDING")} />
                      <StatusTile label={statusLabel("APPROVED", locale)} value={stats.reservations.approved} className={statusBadgeClass("APPROVED")} />
                      <StatusTile label={statusLabel("REJECTED", locale)} value={stats.reservations.rejected} className={statusBadgeClass("REJECTED")} />
                      <StatusTile label={statusLabel("CANCELLED", locale)} value={stats.reservations.cancelled} className={statusBadgeClass("CANCELLED")} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t("admin.usersByRole")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      {(["ADMIN", "OWNER", "USER"] as const).map((r) => {
                        const count =
                          stats.roleBreakdown.find((b) => b.role === r)?._count._all ?? 0;
                        return (
                          <div key={r} className="rounded-lg border bg-card p-3 text-center">
                            <Badge className={`mb-2 ${roleBadgeClass(r)}`} variant="outline">
                              {roleLabel(r, locale)}
                            </Badge>
                            <div className="text-2xl font-semibold">{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <div className="mb-4 flex justify-end">
            <Button onClick={openNewUser} className="gap-2">
              <UsersIcon className="h-4 w-4" /> {t("admin.addUser")}
            </Button>
          </div>

          {usersLoading ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : (
            <div className="overflow-x-auto rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.colName")}</TableHead>
                    <TableHead>{t("admin.colUsername")}</TableHead>
                    <TableHead>{t("admin.colEmail")}</TableHead>
                    <TableHead>{t("admin.colRole")}</TableHead>
                    <TableHead className="text-center">{t("admin.colProperties")}</TableHead>
                    <TableHead className="text-center">{t("admin.colReservations")}</TableHead>
                    <TableHead>{t("admin.colStatus")}</TableHead>
                    <TableHead className="text-right">{t("admin.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">
                        {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                      </TableCell>
                      <TableCell className="text-muted-foreground">@{u.username}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={roleBadgeClass(u.role)} variant="outline">
                          {roleLabel(u.role, locale)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{u._count?.products ?? 0}</TableCell>
                      <TableCell className="text-center">{u._count?.reservations ?? 0}</TableCell>
                      <TableCell>
                        {u.active ? (
                          <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                            {t("admin.active")}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-zinc-300 text-zinc-500">
                            {t("admin.disabled")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => toggleActive(u)}
                            title={u.active ? "Disable" : "Enable"}
                          >
                            <Power className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => openEditUser(u.id)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteUserId(u.id)}
                            disabled={u.id === user?.id}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* RESERVATIONS */}
        <TabsContent value="reservations" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">{t("admin.validateAny")}</p>
          {pendingRes.length === 0 ? (
            <div className="rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">
              {t("admin.noPending")}
            </div>
          ) : (
            pendingRes.map((r) => (
              <AdminReservationRow
                key={r.id}
                r={r}
                onApprove={() => decide(r.id, "approve")}
                onReject={() => decide(r.id, "reject")}
              />
            ))
          )}
          <h3 className="pt-4 text-sm font-semibold">{t("admin.recentDecisions")}</h3>
          {reservations
            .filter((r) => r.status !== "PENDING")
            .slice(0, 8)
            .map((r) => (
              <AdminReservationRow key={r.id} r={r} readonly />
            ))}
        </TabsContent>
        {/* PERMISSIONS MATRIX (admin only) */}
        <TabsContent value="permissions" className="mt-4">
          <PermissionsMatrix />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteUserId} onOpenChange={(o) => !o && setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.deleteUserTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.deleteUserBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusTile({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className={`rounded-lg border p-3 text-center ${className}`}>
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function AdminReservationRow({
  r,
  onApprove,
  onReject,
  readonly,
}: {
  r: Reservation;
  onApprove?: () => void;
  onReject?: () => void;
  readonly?: boolean;
}) {
  const nights = nightsBetween(r.startDate, r.endDate);
  const openProduct = useAppStore((s) => s.openProduct);
  const { t, locale } = useI18n();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-3">
          <div className="aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-lg bg-muted">
            {r.product?.images?.[0]?.url ? (
               
              <img
                src={r.product.images[0].url}
                alt={r.product.title}
                className="h-full w-full cursor-pointer object-cover"
                onClick={() => r.product && openProduct(r.product.id)}
              />
            ) : null}
          </div>
          <div className="min-w-0">
            <button
              onClick={() => r.product && openProduct(r.product.id)}
              className="text-left font-medium hover:underline"
            >
              {r.product?.title ?? t("common.loading")}
            </button>
            <div className="text-sm text-muted-foreground">
              {formatDate(r.startDate, locale)} → {formatDate(r.endDate, locale)} · {nights} {nightLabel(nights, locale)} · {formatPrice(r.totalPrice)}
            </div>
            <div className="text-sm">
              {r.guestName} <span className="text-muted-foreground">·</span> {r.user?.email ?? r.guestEmail}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge className={statusBadgeClass(r.status)}>{statusLabel(r.status, locale)}</Badge>
          {!readonly && r.status === "PENDING" && (
            <>
              <Button size="sm" variant="outline" onClick={onReject} className="gap-1">
                <X className="h-4 w-4" /> {t("host.decline")}
              </Button>
              <Button size="sm" onClick={onApprove} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                <Check className="h-4 w-4" /> {t("host.approve")}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
